import React, { useState, useMemo } from "react";
import {
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import ReportPdfLayout from "../../../Components/ReportPdfLayout";

const LaboratoryReport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [reportType, setReportType] = useState("monthly");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.append("report_type", reportType);
    return p.toString();
  }, [reportType]);

  const endpoint = `laboratory-reports/?${params}`;
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["laboratory-report", params],
    endpoint,
    token
  );

  const handleApply = () => refetch();

  return (
    <Grid container maxWidth="xl" sx={{ p: 3, width: "100%" }}>
      <Grid item xs={12}>
        <ReportPdfLayout
          reportTitle={t("reports.laboratoryReport")}
          fileName="laboratory-report.pdf"
          disabled={isLoading || isError || !data}
        >
          <Grid container>
            <Grid item xs={12} className="no-print">
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {t("reports.laboratoryReport")}
              </Typography>
            </Grid>
            <Grid item xs={12} mb={2} className="no-print">
              <Paper sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("reports.reportType")}</InputLabel>
                  <Select
                    value={reportType}
                    label={t("reports.reportType")}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <MenuItem value="daily">{t("reports.daily")}</MenuItem>
                    <MenuItem value="weekly">{t("reports.weekly")}</MenuItem>
                    <MenuItem value="monthly">{t("reports.monthly")}</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={handleApply}>
                  {t("reports.apply")}
                </Button>
              </Paper>
            </Grid>
            {isError && (
              <Grid item xs={12}>
                <Alert severity="error">{error?.message || t("reports.errorLoading")}</Alert>
              </Grid>
            )}
            {isLoading && (
              <Grid item xs={12} display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Grid>
            )}
            {!isLoading && !isError && data && (
              <>
                {data.date_range && (
                  <Grid item xs={12} mb={2}>
                    <Typography variant="body1">
                      {t("reports.dateRange")}: {data.date_range.start} — {data.date_range.end}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("reports.totalTests")}
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {data.total_tests ?? 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, borderLeft: "4px solid", borderColor: "warning.main" }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("reports.pendingTests")}
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {data.pending_tests ?? 0}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </ReportPdfLayout>
      </Grid>
    </Grid>
  );
};

export default LaboratoryReport;
